import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { ChevronDown } from 'lucide-react';
import { getQawaemPortalRoot } from './qawaemPortalRoot';

/**
 * Clickable status pill with dropdown — change status on select.
 */
export default function QawaemClickableStatusBadge({
  status,
  recordId,
  options = [],
  onChange,
  title,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const buttonRef = useRef(null);

  const config = options.find((s) => s.value === status) || options[0] || {
    value: status,
    label: status || '—',
    icon: () => null,
    style: 'bg-white/10 text-white/70 border-white/20',
  };
  const { label, icon: Icon, style } = config;

  const close = () => {
    setOpen(false);
    setPosition(null);
  };

  const handleToggle = (e) => {
    e.stopPropagation();
    if (disabled || !onChange || options.length === 0) return;

    if (open) {
      close();
      return;
    }

    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const dropdownWidth = 200;
    const dropdownHeight = Math.min(options.length * 44 + 16, 220);

    let left = rect.left + rect.width / 2;
    let top = rect.bottom + 8;

    if (left + dropdownWidth / 2 > window.innerWidth - 20) {
      left = window.innerWidth - dropdownWidth - 20;
    }
    if (left - dropdownWidth / 2 < 20) {
      left = dropdownWidth / 2 + 20;
    }
    if (top + dropdownHeight > window.innerHeight - 20) {
      top = rect.top - dropdownHeight - 8;
    }

    setPosition({ top, left });
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return undefined;

    const handleClickOutside = (event) => {
      const dropdown = document.querySelector(`[data-status-dropdown-for="${recordId}"]`);
      if (
        buttonRef.current
        && !buttonRef.current.contains(event.target)
        && (!dropdown || !dropdown.contains(event.target))
      ) {
        close();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, recordId]);

  useEffect(() => () => close(), []);

  const handleSelect = (value) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    close();
    if (value !== status && onChange) {
      onChange(recordId, value);
    }
  };

  if (!onChange || options.length === 0) {
    return (
      <span className={`px-3 py-1.5 rounded-full text-xs font-medium inline-flex items-center gap-1.5 border ${style}`}>
        {Icon ? <Icon size={14} /> : null}
        {label}
      </span>
    );
  }

  return (
    <div className="relative inline-block">
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${style} hover:opacity-80 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed`}
        title={title}
      >
        {Icon ? <Icon size={14} /> : null}
        {label}
        <ChevronDown size={12} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && position && (() => {
        const portalRoot = getQawaemPortalRoot();
        if (!portalRoot) return null;
        return ReactDOM.createPortal(
        <div
          key={`qawaem-status-menu-${recordId}`}
          data-status-dropdown-for={recordId}
          className="border border-blue-400/60 rounded-2xl shadow-2xl overflow-hidden min-w-[200px] transform -translate-x-1/2"
          style={{
            position: 'fixed',
            zIndex: 99999,
            top: `${position.top}px`,
            left: `${position.left}px`,
            backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))',
            boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)',
          }}
        >
          <div className="p-2">
            {options.map((option) => {
              const OptionIcon = option.icon;
              return (
                <button
                  key={option.value}
                  type="button"
                  onMouseDown={handleSelect(option.value)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 hover:bg-blue-500/20 transition-all text-left rounded-xl ${option.value === status ? 'bg-blue-500/25 shadow-inner' : ''}`}
                >
                  {OptionIcon ? <OptionIcon size={16} className={option.style?.split(' ').find((c) => c.startsWith('text-')) || 'text-white'} /> : null}
                  <span className="text-white text-sm font-medium">{option.label}</span>
                </button>
              );
            })}
          </div>
        </div>,
        portalRoot,
        );
      })()}
    </div>
  );
}
